import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  ArrowDown,
  Building2,
  CalendarRange,
  Filter,
  Presentation,
  RefreshCw,
  Search,
  Tractor,
} from 'lucide-react';
import {
  getAllMachines,
  getCompanies,
  getErrorSummary,
  getErrorTypes,
  getUsageSummary,
} from '../api/api';
import AnalyticsSummaryModal from '../components/AnalyticsSummaryModal';
import DashboardCalendars from '../components/DashboardCalendars';
import ErrorTypeShareCard from '../components/ErrorTypeShareCard';
import UptimeRatioCard from '../components/UptimeRatioCard';
import UtilizationCard from '../components/UtilizationCard';
import { NO_COMPANY } from '../components/CompanyScopePicker';
import { getErrorTypeLabel } from '../components/dashboardSummaryUtils';
import { ActionDrawer, Toast } from '../components/ui';
import styles from './Analytics.module.scss';

const SCOPE_STORAGE_KEY = 'analytics.reportingScope.v1';
const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

function getDefaultDateRange() {
  const now = new Date();
  const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 5, 1));
  return { startDate: start.toISOString().split('T')[0], endDate: now.toISOString().split('T')[0] };
}

function getDefaultScope() {
  return {
    ...getDefaultDateRange(),
    companyIds: [],
    machineIds: [],
    excludedErrorTypes: [],
    skipInstalledAfterStart: false,
  };
}

// A machine installed after the reporting period started has no chance of a
// full month of data, so the scope can opt to leave it out of every figure.
function isInstalledAfter(machine, startDate) {
  return Boolean(machine.installDate && startDate && machine.installDate > startDate);
}

// The applied scope survives reloads. Anything malformed falls back to the defaults.
function loadStoredScope() {
  const defaults = getDefaultScope();
  try {
    const raw = window.localStorage.getItem(SCOPE_STORAGE_KEY);
    if (!raw) {
      return defaults;
    }
    const stored = JSON.parse(raw);
    const isStringList = value => Array.isArray(value) && value.every(v => typeof v === 'string');
    return {
      startDate: DATE_PATTERN.test(stored.startDate) ? stored.startDate : defaults.startDate,
      endDate: DATE_PATTERN.test(stored.endDate) ? stored.endDate : defaults.endDate,
      companyIds: isStringList(stored.companyIds) ? stored.companyIds : [],
      machineIds:
        Array.isArray(stored.machineIds) && stored.machineIds.every(Number.isInteger)
          ? stored.machineIds
          : [],
      excludedErrorTypes: isStringList(stored.excludedErrorTypes) ? stored.excludedErrorTypes : [],
      skipInstalledAfterStart: stored.skipInstalledAfterStart === true,
    };
  } catch {
    return defaults;
  }
}

function storeScope(scope) {
  try {
    window.localStorage.setItem(SCOPE_STORAGE_KEY, JSON.stringify(scope));
  } catch {
    // Storage may be unavailable (private mode, quota); the page still works without it.
  }
}

function Analytics() {
  // Only the very first fetch swaps the cards for placeholders. Later fetches
  // (auto-refresh, manual refresh, scope changes) keep the current content on
  // screen and just show a small "Updating" indicator, so nothing flashes or
  // loses its scroll position.
  const [initialLoading, setInitialLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const hasLoadedRef = useRef(false);
  const [usageSummary, setUsageSummary] = useState(null);
  const [errorSummary, setErrorSummary] = useState(null);
  const [machineList, setMachineList] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [errorTypes, setErrorTypes] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [machineSearch, setMachineSearch] = useState('');
  // appliedFilters.machineIds uses API semantics: [] means "all machines".
  // draftFilters.machineIds is always the explicit list shown as checked in the drawer.
  // excludedErrorTypes is an exclusion list so newly appearing types are included by default.
  const [draftFilters, setDraftFilters] = useState(getDefaultScope);
  const [appliedFilters, setAppliedFilters] = useState(loadStoredScope);

  useEffect(() => {
    getCompanies()
      .then(response => setCompanies(response.data || []))
      .catch(() => setCompanies([]));
    getErrorTypes()
      .then(response => setErrorTypes(response.data?.error_types || []))
      .catch(() => setErrorTypes([]));
  }, []);

  useEffect(() => {
    storeScope(appliedFilters);
  }, [appliedFilters]);

  const fetchAnalytics = useCallback(async () => {
    try {
      if (hasLoadedRef.current) {
        setRefreshing(true);
      } else {
        setInitialLoading(true);
      }
      // Machines come first: the install-date rule needs them to work out
      // which IDs the summaries should cover.
      const machinesResponse = await getAllMachines();
      const machines = (machinesResponse.data || []).map(machine => ({
        id: machine.id,
        serial: machine.serial,
        nickname: machine.nickname,
        companyId: machine.company_id,
        installDate: machine.install_date || null,
      }));
      setMachineList(machines);

      let machineIds = appliedFilters.machineIds.length ? appliedFilters.machineIds : null;
      if (appliedFilters.skipInstalledAfterStart) {
        machineIds = machines
          .filter(machine => !machineIds || machineIds.includes(machine.id))
          .filter(machine => !isInstalledAfter(machine, appliedFilters.startDate))
          .map(machine => machine.id);
        if (machineIds.length === 0) {
          // An empty list would mean "all machines" to the API; there is
          // simply nothing to report for this scope.
          setUsageSummary(null);
          setErrorSummary(null);
          setErrorMessage('');
          return;
        }
      }
      const [usageResponse, errorResponse] = await Promise.all([
        getUsageSummary({
          startDate: appliedFilters.startDate,
          endDate: appliedFilters.endDate,
          machineIds,
        }),
        getErrorSummary({
          startDate: appliedFilters.startDate,
          endDate: appliedFilters.endDate,
          machineIds,
          excludeErrorTypes: appliedFilters.excludedErrorTypes,
        }),
      ]);
      setUsageSummary(usageResponse.data || null);
      setErrorSummary(errorResponse.data || null);
      setErrorMessage('');
    } catch (error) {
      setErrorMessage(error.response?.data?.detail || error.message || 'Failed to load analytics.');
    } finally {
      hasLoadedRef.current = true;
      setInitialLoading(false);
      setRefreshing(false);
    }
  }, [appliedFilters]);

  useEffect(() => {
    fetchAnalytics();
    const intervalId = setInterval(fetchAnalytics, 60000);
    return () => clearInterval(intervalId);
  }, [fetchAnalytics]);

  const allMachineIds = useMemo(() => machineList.map(machine => machine.id), [machineList]);

  // Companies shown in step 1, each with the machines it owns. "Unassigned" only
  // appears when there are machines without a company.
  const companyOptions = useMemo(() => {
    const byCompany = new Map();
    machineList.forEach(machine => {
      const key = machine.companyId || NO_COMPANY;
      byCompany.set(key, [...(byCompany.get(key) || []), machine.id]);
    });
    const options = companies.map(company => ({
      id: company.company_id,
      name: company.name,
      tag: company.tag || null,
      machineIds: byCompany.get(company.company_id) || [],
    }));
    if ((byCompany.get(NO_COMPANY) || []).length > 0) {
      options.push({
        id: NO_COMPANY,
        name: 'Unassigned company',
        tag: null,
        machineIds: byCompany.get(NO_COMPANY),
      });
    }
    return options;
  }, [companies, machineList]);
  const companyTags = useMemo(
    () => [...new Set(companyOptions.map(option => option.tag).filter(Boolean))].sort(),
    [companyOptions]
  );
  const companyNameById = useMemo(
    () => new Map(companyOptions.map(option => [option.id, option.name])),
    [companyOptions]
  );

  const machineIdsOfCompanies = companyIds =>
    companyOptions
      .filter(option => companyIds.includes(option.id))
      .flatMap(option => option.machineIds);

  // Step 1: selecting a company narrows the machine pool to that company and
  // selects its machines; deselecting removes them. No company = every machine.
  const toggleCompanies = ids => {
    if (!ids.length) {
      return;
    }
    setDraftFilters(current => {
      const allSelected = ids.every(id => current.companyIds.includes(id));
      const companyIds = allSelected
        ? current.companyIds.filter(id => !ids.includes(id))
        : [...new Set([...current.companyIds, ...ids])];
      const affected = machineIdsOfCompanies(ids);
      let machineIds;
      if (companyIds.length === 0) {
        machineIds = allMachineIds;
      } else if (allSelected) {
        machineIds = current.machineIds.filter(id => !affected.includes(id));
      } else {
        const pool = machineIdsOfCompanies(companyIds);
        machineIds = [
          ...new Set([...current.machineIds.filter(id => pool.includes(id)), ...affected]),
        ];
      }
      return { ...current, companyIds, machineIds };
    });
  };
  const clearCompanies = () =>
    setDraftFilters(current => ({ ...current, companyIds: [], machineIds: allMachineIds }));
  const isTagActive = tag => {
    const ids = companyOptions.filter(option => option.tag === tag).map(option => option.id);
    return ids.length > 0 && ids.every(id => draftFilters.companyIds.includes(id));
  };

  // Step 2: machines inside the current company scope.
  const scopedMachines = useMemo(() => {
    if (draftFilters.companyIds.length === 0) {
      return machineList;
    }
    return machineList.filter(machine =>
      draftFilters.companyIds.includes(machine.companyId || NO_COMPANY)
    );
  }, [machineList, draftFilters.companyIds]);
  const visibleMachines = useMemo(() => {
    const query = machineSearch.trim().toLowerCase();
    if (!query) {
      return scopedMachines;
    }
    return scopedMachines.filter(
      machine =>
        (machine.nickname || '').toLowerCase().includes(query) ||
        (machine.serial || '').toLowerCase().includes(query)
    );
  }, [scopedMachines, machineSearch]);
  const scopedSelectedCount = scopedMachines.filter(machine =>
    draftFilters.machineIds.includes(machine.id)
  ).length;

  const toggleMachine = id =>
    setDraftFilters(current => ({
      ...current,
      machineIds: current.machineIds.includes(id)
        ? current.machineIds.filter(machineId => machineId !== id)
        : [...current.machineIds, id],
    }));
  const setVisibleMachinesSelected = selected => {
    const visibleIds = visibleMachines.map(machine => machine.id);
    setDraftFilters(current => ({
      ...current,
      machineIds: selected
        ? [...new Set([...current.machineIds, ...visibleIds])]
        : current.machineIds.filter(id => !visibleIds.includes(id)),
    }));
  };

  const openScopeDrawer = () => {
    setDraftFilters({
      ...appliedFilters,
      companyIds: [...appliedFilters.companyIds],
      machineIds: appliedFilters.machineIds.length ? [...appliedFilters.machineIds] : allMachineIds,
      excludedErrorTypes: [...appliedFilters.excludedErrorTypes],
      skipInstalledAfterStart: appliedFilters.skipInstalledAfterStart,
    });
    setMachineSearch('');
    setFilterOpen(true);
  };

  const invalidDateRange =
    Boolean(draftFilters.startDate) &&
    Boolean(draftFilters.endDate) &&
    draftFilters.startDate > draftFilters.endDate;
  // Machines the draft would leave out because they were installed after the
  // draft start date. Applying is blocked when that empties the scope.
  const draftSkippedCount = draftFilters.skipInstalledAfterStart
    ? machineList.filter(
        machine =>
          draftFilters.machineIds.includes(machine.id) &&
          isInstalledAfter(machine, draftFilters.startDate)
      ).length
    : 0;
  const draftSkipsEverything =
    draftFilters.machineIds.length > 0 && draftSkippedCount === draftFilters.machineIds.length;
  const canApply = draftFilters.machineIds.length > 0 && !invalidDateRange && !draftSkipsEverything;

  const applyFilters = () => {
    if (!canApply) {
      return;
    }
    const selectsEverything =
      allMachineIds.length > 0 && allMachineIds.every(id => draftFilters.machineIds.includes(id));
    setAppliedFilters({
      startDate: draftFilters.startDate,
      endDate: draftFilters.endDate,
      companyIds: [...draftFilters.companyIds],
      machineIds: selectsEverything ? [] : [...draftFilters.machineIds],
      excludedErrorTypes: [...draftFilters.excludedErrorTypes],
      skipInstalledAfterStart: draftFilters.skipInstalledAfterStart,
    });
    setFilterOpen(false);
  };

  // Error type scope: everything listed by the API minus the exclusions.
  const knownErrorTypes = useMemo(() => errorTypes.map(item => item.error_type), [errorTypes]);
  const draftHiddenTypeCount = knownErrorTypes.filter(type =>
    draftFilters.excludedErrorTypes.includes(type)
  ).length;
  const appliedHiddenTypeCount = knownErrorTypes.filter(type =>
    appliedFilters.excludedErrorTypes.includes(type)
  ).length;
  const toggleErrorType = type =>
    setDraftFilters(current => ({
      ...current,
      excludedErrorTypes: current.excludedErrorTypes.includes(type)
        ? current.excludedErrorTypes.filter(item => item !== type)
        : [...current.excludedErrorTypes, type],
    }));
  const setAllErrorTypesIncluded = included =>
    setDraftFilters(current => ({
      ...current,
      excludedErrorTypes: included ? [] : [...knownErrorTypes],
    }));

  // Machines the applied scope covers: the explicit selection (or everything),
  // minus the ones installed after the start date when that rule is on.
  const selectedMachineList = useMemo(
    () =>
      appliedFilters.machineIds.length
        ? machineList.filter(machine => appliedFilters.machineIds.includes(machine.id))
        : machineList,
    [machineList, appliedFilters.machineIds]
  );
  const skippedMachineCount = appliedFilters.skipInstalledAfterStart
    ? selectedMachineList.filter(machine => isInstalledAfter(machine, appliedFilters.startDate))
        .length
    : 0;
  const scopedMachineList = useMemo(
    () =>
      appliedFilters.skipInstalledAfterStart
        ? selectedMachineList.filter(
            machine => !isInstalledAfter(machine, appliedFilters.startDate)
          )
        : selectedMachineList,
    [selectedMachineList, appliedFilters.skipInstalledAfterStart, appliedFilters.startDate]
  );
  // Machine IDs in API semantics ([] = all). Once the install-date rule drops
  // machines the list has to be explicit so drill-downs match the charts.
  // Keyed on the joined IDs so the periodic machine refresh does not hand the
  // modals a new array (and a new fetch) when nothing actually changed.
  const scopedMachineKey = skippedMachineCount
    ? scopedMachineList.map(machine => machine.id).join(',')
    : '';
  const effectiveMachineIds = useMemo(
    () => (scopedMachineKey ? scopedMachineKey.split(',').map(Number) : appliedFilters.machineIds),
    [scopedMachineKey, appliedFilters.machineIds]
  );

  // Scope handed to the drill-down modals so they show exactly what the page shows.
  const detailScope = useMemo(
    () => ({
      machineIds: effectiveMachineIds,
      excludedErrorTypes: appliedFilters.excludedErrorTypes,
      errorTypeOptions: knownErrorTypes
        .filter(type => !appliedFilters.excludedErrorTypes.includes(type))
        .map(type => ({ value: type, label: getErrorTypeLabel(type) })),
    }),
    [effectiveMachineIds, appliedFilters.excludedErrorTypes, knownErrorTypes]
  );
  const summaryScope = useMemo(
    () => ({ ...appliedFilters, machineIds: effectiveMachineIds }),
    [appliedFilters, effectiveMachineIds]
  );
  const errorTypeScopeLabel = appliedHiddenTypeCount
    ? `${knownErrorTypes.length - appliedHiddenTypeCount} of ${knownErrorTypes.length} types`
    : 'All error types';

  const selectedMachineLabel = (() => {
    if (!appliedFilters.machineIds.length && !skippedMachineCount) {
      return 'All machines';
    }
    const companyCount = appliedFilters.companyIds.length;
    let label = `${scopedMachineList.length} of ${machineList.length} machines`;
    if (companyCount === 1) {
      label += ` · ${companyNameById.get(appliedFilters.companyIds[0]) || '1 company'}`;
    } else if (companyCount > 1) {
      label += ` · ${companyCount} companies`;
    }
    if (skippedMachineCount) {
      label += ` · ${skippedMachineCount} installed after start skipped`;
    }
    return label;
  })();

  return (
    <div className={styles.page}>
      <header className={styles.pageHeader}>
        <div>
          <span className={styles.eyebrow}>Fleet intelligence</span>
          <h1>Analytics</h1>
          <p>
            Compare machine utilization, uptime, and incident patterns over one consistent scope.
          </p>
        </div>
        <div className={styles.headerActions}>
          <button type="button" className={styles.secondaryButton} onClick={openScopeDrawer}>
            <Filter size={15} /> Change scope
          </button>
          <button
            type="button"
            className={styles.secondaryButton}
            onClick={() => setSummaryOpen(true)}
            disabled={initialLoading || !usageSummary}
          >
            <Presentation size={15} /> Summary
          </button>
          <button
            type="button"
            className={styles.refreshButton}
            onClick={fetchAnalytics}
            disabled={initialLoading || refreshing}
          >
            <RefreshCw size={15} className={refreshing ? styles.spinning : ''} />{' '}
            {refreshing ? 'Refreshing' : 'Refresh'}
          </button>
        </div>
      </header>
      {errorMessage && (
        <Toast tone="error" onClose={() => setErrorMessage('')}>
          {errorMessage}
        </Toast>
      )}
      <section className={styles.scopeBar}>
        <CalendarRange size={16} />
        <div>
          <span>Reporting period</span>
          <strong>
            {appliedFilters.startDate} → {appliedFilters.endDate}
          </strong>
        </div>
        <div>
          <span>Fleet scope</span>
          <strong>{selectedMachineLabel}</strong>
        </div>
        <div>
          <span>Error types</span>
          <strong>{errorTypeScopeLabel}</strong>
        </div>
        <div className={styles.scopeActions}>
          {refreshing && (
            <span className={styles.updatingPill} role="status" aria-live="polite">
              <RefreshCw size={12} className={styles.spinning} /> Updating
            </span>
          )}
          <button type="button" onClick={openScopeDrawer}>
            Edit
          </button>
        </div>
      </section>

      <section className={styles.calendarPanel}>
        <DashboardCalendars
          usageData={usageSummary}
          errorData={errorSummary}
          machines={scopedMachineList}
          scope={detailScope}
          loading={initialLoading}
          dateRange={{ startDate: appliedFilters.startDate, endDate: appliedFilters.endDate }}
        />
      </section>
      <div className={styles.analyticsGrid}>
        <UptimeRatioCard
          usageData={usageSummary}
          errorData={errorSummary}
          loading={initialLoading}
        />
        <ErrorTypeShareCard
          errorData={errorSummary}
          machines={scopedMachineList}
          scope={detailScope}
          loading={initialLoading}
        />
      </div>
      <div className={styles.analyticsGrid}>
        <UtilizationCard
          usageData={usageSummary}
          errorData={errorSummary}
          machineCount={scopedMachineList.length}
          startDate={appliedFilters.startDate}
          endDate={appliedFilters.endDate}
          loading={initialLoading}
        />
      </div>

      {summaryOpen && (
        <AnalyticsSummaryModal
          usageData={usageSummary}
          errorData={errorSummary}
          scope={summaryScope}
          machineCount={scopedMachineList.length}
          onClose={() => setSummaryOpen(false)}
        />
      )}

      <ActionDrawer
        open={filterOpen}
        eyebrow="Analytics scope"
        title="Choose reporting scope"
        description="Draft changes do not reload charts until you apply them."
        onClose={() => setFilterOpen(false)}
        footer={
          <>
            <button
              type="button"
              className={styles.secondaryButton}
              onClick={() => setFilterOpen(false)}
            >
              Cancel
            </button>
            <button
              type="button"
              className={styles.primaryButton}
              onClick={applyFilters}
              disabled={!canApply}
            >
              Apply scope
            </button>
          </>
        }
      >
        <div className={styles.filterForm}>
          <div className={styles.dateGrid}>
            <label>
              <span>From</span>
              <input
                type="date"
                value={draftFilters.startDate}
                onChange={event =>
                  setDraftFilters(current => ({ ...current, startDate: event.target.value }))
                }
              />
            </label>
            <label>
              <span>To</span>
              <input
                type="date"
                value={draftFilters.endDate}
                onChange={event =>
                  setDraftFilters(current => ({ ...current, endDate: event.target.value }))
                }
              />
            </label>
          </div>
          {invalidDateRange && (
            <p className={styles.formHint}>The start date must be on or before the end date.</p>
          )}
          <label className={styles.toggleRow}>
            <input
              type="checkbox"
              checked={draftFilters.skipInstalledAfterStart}
              onChange={event =>
                setDraftFilters(current => ({
                  ...current,
                  skipInstalledAfterStart: event.target.checked,
                }))
              }
            />
            <span>
              <strong>Skip machines installed after the start date</strong>
              {draftFilters.skipInstalledAfterStart && (
                <small>
                  {draftSkipsEverything &&
                    'Every selected machine was installed after the start date · pick an earlier date or turn this off'}
                  {!draftSkipsEverything &&
                    (draftSkippedCount
                      ? `${draftSkippedCount} of ${draftFilters.machineIds.length} selected machine${draftFilters.machineIds.length === 1 ? '' : 's'} installed after ${draftFilters.startDate} will be left out`
                      : 'None of the selected machines were installed after the start date')}
                </small>
              )}
            </span>
          </label>

          <section className={styles.scopeSection}>
            <div className={styles.scopeHeader}>
              <span className={styles.stepBadge}>1</span>
              <div>
                <strong>
                  <Building2 size={13} /> Companies
                </strong>
                <small>
                  {draftFilters.companyIds.length
                    ? `${draftFilters.companyIds.length} of ${companyOptions.length} selected`
                    : 'All companies · pick some to narrow the machine list'}
                </small>
              </div>
              <button
                type="button"
                onClick={clearCompanies}
                disabled={draftFilters.companyIds.length === 0}
              >
                Clear
              </button>
            </div>
            {companyTags.length > 0 && (
              <div className={styles.tagFilters}>
                <span>Quick select by tag</span>
                {companyTags.map(tag => (
                  <button
                    type="button"
                    key={tag}
                    className={isTagActive(tag) ? styles.tagActive : ''}
                    aria-pressed={isTagActive(tag)}
                    onClick={() =>
                      toggleCompanies(
                        companyOptions.filter(option => option.tag === tag).map(option => option.id)
                      )
                    }
                  >
                    {tag}
                  </button>
                ))}
              </div>
            )}
            <div className={styles.optionList}>
              {companyOptions.map(option => (
                <label key={option.id}>
                  <input
                    type="checkbox"
                    checked={draftFilters.companyIds.includes(option.id)}
                    onChange={() => toggleCompanies([option.id])}
                  />
                  <span>
                    <strong>{option.name}</strong>
                    <small>
                      {option.machineIds.length} machine{option.machineIds.length === 1 ? '' : 's'}
                    </small>
                  </span>
                  {option.tag && <em>{option.tag}</em>}
                </label>
              ))}
              {companyOptions.length === 0 && <p className={styles.emptyList}>No companies</p>}
            </div>
          </section>

          <div className={styles.scopeConnector}>
            <ArrowDown size={14} />
            <span>
              {scopedMachines.length} machine{scopedMachines.length === 1 ? '' : 's'} from{' '}
              {draftFilters.companyIds.length
                ? `${draftFilters.companyIds.length} compan${draftFilters.companyIds.length === 1 ? 'y' : 'ies'}`
                : 'all companies'}
            </span>
          </div>

          <section className={styles.scopeSection}>
            <div className={styles.scopeHeader}>
              <span className={styles.stepBadge}>2</span>
              <div>
                <strong>
                  <Tractor size={13} /> Machines
                </strong>
                <small>
                  {scopedSelectedCount} of {scopedMachines.length} selected
                  {draftFilters.machineIds.length === 0 && ' · select at least one'}
                </small>
              </div>
              <div className={styles.headerButtons}>
                <button
                  type="button"
                  onClick={() => setVisibleMachinesSelected(true)}
                  disabled={visibleMachines.every(machine =>
                    draftFilters.machineIds.includes(machine.id)
                  )}
                >
                  Select all
                </button>
                <button
                  type="button"
                  onClick={() => setVisibleMachinesSelected(false)}
                  disabled={
                    !visibleMachines.some(machine => draftFilters.machineIds.includes(machine.id))
                  }
                >
                  Clear
                </button>
              </div>
            </div>
            <label className={styles.searchBox}>
              <Search size={14} />
              <input
                type="search"
                placeholder="Search by name or serial"
                value={machineSearch}
                onChange={event => setMachineSearch(event.target.value)}
              />
            </label>
            <div className={styles.optionList}>
              {visibleMachines.map(machine => {
                const willSkip =
                  draftFilters.skipInstalledAfterStart &&
                  draftFilters.machineIds.includes(machine.id) &&
                  isInstalledAfter(machine, draftFilters.startDate);
                const details = [
                  machine.nickname && machine.serial,
                  willSkip && `installed ${machine.installDate} · skipped`,
                ].filter(Boolean);
                return (
                  <label key={machine.id} className={willSkip ? styles.skippedOption : ''}>
                    <input
                      type="checkbox"
                      checked={draftFilters.machineIds.includes(machine.id)}
                      onChange={() => toggleMachine(machine.id)}
                    />
                    <span>
                      <strong>{machine.nickname || machine.serial}</strong>
                      {details.length > 0 && <small>{details.join(' · ')}</small>}
                    </span>
                    {draftFilters.companyIds.length !== 1 && (
                      <em>
                        {companyNameById.get(machine.companyId || NO_COMPANY) || 'Unassigned'}
                      </em>
                    )}
                  </label>
                );
              })}
              {visibleMachines.length === 0 && (
                <p className={styles.emptyList}>
                  {machineSearch ? 'No machines match the search' : 'No machines in this scope'}
                </p>
              )}
            </div>
          </section>

          <section className={styles.scopeSection}>
            <div className={styles.scopeHeader}>
              <span className={`${styles.stepBadge} ${styles.stepBadgeAlt}`}>
                <AlertTriangle size={11} />
              </span>
              <div>
                <strong>Error types</strong>
                <small>
                  {draftHiddenTypeCount
                    ? `${draftHiddenTypeCount} of ${knownErrorTypes.length} hidden · hidden errors are left out of every chart and total`
                    : 'All error types included'}
                </small>
              </div>
              <div className={styles.headerButtons}>
                <button
                  type="button"
                  onClick={() => setAllErrorTypesIncluded(true)}
                  disabled={draftHiddenTypeCount === 0}
                >
                  Include all
                </button>
                <button
                  type="button"
                  onClick={() => setAllErrorTypesIncluded(false)}
                  disabled={draftHiddenTypeCount === knownErrorTypes.length}
                >
                  Exclude all
                </button>
              </div>
            </div>
            <div className={styles.optionList}>
              {errorTypes.map(item => (
                <label key={item.error_type}>
                  <input
                    type="checkbox"
                    checked={!draftFilters.excludedErrorTypes.includes(item.error_type)}
                    onChange={() => toggleErrorType(item.error_type)}
                  />
                  <span>
                    <strong>{getErrorTypeLabel(item.error_type)}</strong>
                    <small>{item.error_type}</small>
                  </span>
                  <em>
                    {item.session_count} session{item.session_count === 1 ? '' : 's'}
                  </em>
                </label>
              ))}
              {errorTypes.length === 0 && (
                <p className={styles.emptyList}>No error types recorded yet</p>
              )}
            </div>
          </section>
        </div>
      </ActionDrawer>
    </div>
  );
}

export default Analytics;
